// ScreenerAnnouncement.imp.jsx (header count reflects filter)
import React, { useMemo, useState, useRef, useEffect } from "react";
import "./css/ScreenerAnnouncement.css";
import { useScreenerAnnouncementFeed } from "@dashboard/screenerannouncements/context/ScreenerAnnContext";
import TabBuilder from "@dashboard/screenerannouncements/TabBuilder";
import ConfirmDialog from "@dashboard/utils/ConfirmDialog";
import {
    compareAnnouncementsDesc,
    formatLocalDate,
    normYYYYMMDD,
    idOf,
    badgeInfo,
} from "./utils/announcementsTime";
import { apiFetch} from "apiClient";
import { apiFetch as fetch } from "apiClient";

const ScreenerAnnouncement = () => {
    const {
        buckets,
        tabRegistry, // already ordered (drag/drop affects this)
        addDynamicTab,
        removeDynamicTab,
        markOld,
        markImpConcallsRead,
        updateDynamicTab,
        dynamicDefs,
        hasScreenerAnnouncementsLoaded,
        moveTab,            // NEW
        resetTabOrder       // NEW (not used in UI yet, but available)
    } = useScreenerAnnouncementFeed();

    const defaultTab = (tabRegistry && tabRegistry[0]?.name) || "PF Announcements";
    const [activeTab, setActiveTab] = useState(defaultTab);

    // Keep activeTab valid when tabs are reordered / added / deleted
    useEffect(() => {
        const names = new Set((tabRegistry || []).map(t => t.name));
        if (!names.has(activeTab)) {
            setActiveTab(defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabRegistry]);

    // ---- Date filter state (applies to ACTIVE tab only) ----
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState(""); // 'YYYY-MM-DD'
    const [dateTo,   setDateTo]   = useState(""); // 'YYYY-MM-DD'

    const hasDateFilter = !!(dateFrom || dateTo);
    const fromKey = useMemo(() => (dateFrom ? dateFrom.replace(/-/g, "") : null), [dateFrom]);
    const toKey   = useMemo(() => (dateTo   ? dateTo.replace(/-/g,   "") : null), [dateTo]);

    // ---- Actions dropdown ----
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const [showBuilder, setShowBuilder] = useState(false);
    const [builderInitial, setBuilderInitial] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmCfg, setConfirmCfg] = useState({ title: "", message: "", onConfirm: null });

    // The list provided by the active tab
    const baseList = useMemo(() => (buckets?.[activeTab] || []), [buckets, activeTab]);

    // 🔽 always sort newest → oldest by BroadcastTime/announcementDate (with time > no-time)
    const sortedList = useMemo(() => {
        return [...baseList].sort(compareAnnouncementsDesc);
    }, [baseList]);

    // Filter the visible list by the date constraint (inclusive), AFTER sorting
    const filteredList = useMemo(() => {
        if (!hasDateFilter) return sortedList;
        return sortedList.filter((a) => {
            // date filter is date-only, so we use announcementDate for the day bucket
            const k = normYYYYMMDD(a?.announcementDate);
            if (!k) return false;
            if (fromKey && toKey)   return k >= fromKey && k <= toKey;
            if (fromKey && !toKey)  return k === fromKey; // single day
            if (!fromKey && toKey)  return k === toKey;   // single day
            return true;
        });
    }, [sortedList, hasDateFilter, fromKey, toKey]);

    // Per-tab counts (buckets already exclude hidden/marked-old)
    const countsByTab = useMemo(() => {
        const out = {};
        for (const [name, list] of Object.entries(buckets || {})) {
            out[name] = Array.isArray(list) ? list.length : 0;
        }
        return out;
    }, [buckets]);

    const isFilterTab = useMemo(() => {
        const def = tabRegistry.find((t) => t.name === activeTab);
        return def?.type === "filter";
    }, [tabRegistry, activeTab]);

    const safeParse = (t) => { try { return JSON.parse(t ?? "{}"); } catch { return {}; } };
    const chunk = (arr, size) => {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
    };


    const postMarkOld = async (ids) => {
        if (!ids?.length) return { requested: 0, unique: 0, marked: 0 };

        try {
            const res = await fetch('/api/screenerannouncements/markOld', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids }),
            });

            if (!res.ok) {
                console.error("markOld non-OK status=", res.status);
                return { requested: ids.length, unique: ids.length, marked: 0 };
            }

            const data = await res.json().catch(() => null);
            if (data && typeof data.marked === "number") {
                if (data.marked < data.unique) {
                    console.warn("markOld partial:", data);
                }
                return data;
            }

            // fallback if backend returns no JSON for some reason
            return { requested: ids.length, unique: ids.length, marked: ids.length };
        } catch (e) {
            console.error(e);
            return { requested: ids.length, unique: ids.length, marked: 0 };
        }
    };

    const openConfirm = (cfg) => { setConfirmCfg(cfg); setShowConfirm(true); };

    const handleClearOne = async (a) => {
        const id = idOf(a);
        if (!id) return;

        if (a._kind === "impConcall") {
            markImpConcallsRead([a.id]);
            return;
        }

        // Hide immediately for snappy UI
        markOld([id]);

        // Persist
        const res = await postMarkOld([id]);
        if (!res || res.marked < 1) {
            console.warn("Failed to mark old on backend for id=", id, res);
            // Optional: you could unhide here if you want strict consistency.
        }
    };


    const handleMarkAllVisibleOld = async () => {
        const list = filteredList; // respect date filter
        const ids = list
            .map(a => (a?.id != null ? String(a.id) : null))
            .filter(Boolean);

        const missing = list.filter(a => a?.id == null);
        console.log("MarkAll: list=", list.length, "ids=", ids.length, "missingId=", missing.length);
        if (missing.length) console.log("Sample missingId items:", missing.slice(0, 3));

        if (!ids.length) return;

        if (activeTab === "Imp Concalls") {
            markImpConcallsRead(list.map((a) => a.id));
            return;
        }

        // Batch to keep requests safe + reliable
        const BATCH = 100;
        const batches = chunk(ids, BATCH);

        let totalMarked = 0;
        for (const batch of batches) {
            // hide the batch immediately
            markOld(batch);

            // persist and accumulate
            const res = await postMarkOld(batch);
            totalMarked += (res?.marked ?? 0);
        }

        if (totalMarked < ids.length) {
            console.warn(`Bulk markOld partial: requested=${ids.length}, marked=${totalMarked}`);
        }
    };


    const openEditTab = () => {
        const def = tabRegistry.find((t) => t.name === activeTab && t.type === "filter");
        if (!def) return;
        const raw = dynamicDefs.find((d) => d.id === def._id);
        setBuilderInitial({ name: raw?.name ?? def.name, rules: safeParse(raw?.rules || "{}") });
        setShowBuilder(true);
    };

    const handleSaveEdit = async (payload) => {
        const def = tabRegistry.find((t) => t.name === activeTab && t.type === "filter");
        if (!def) return;
        await updateDynamicTab(def.key, { name: payload.name, rules: payload.rules });
        setShowBuilder(false);
        setBuilderInitial(null);
        setActiveTab(payload.name);
    };

    const handleCreateTab = (payload) => {
        addDynamicTab(payload);
        setShowBuilder(false);
        setActiveTab(payload.name);
    };

    const handleDeleteTab = () => {
        const def = tabRegistry.find((t) => t.name === activeTab);
        if (def?.type === "filter") {
            removeDynamicTab(def.key);
            setActiveTab("PF Announcements");
        }
    };

    const clearDateFilter = () => {
        setDateFrom("");
        setDateTo("");
        setDatePickerOpen(false);
    };

    // ----- Header count: reflects filter when applied, else shows full tab count -----
    const headerCount = hasDateFilter ? filteredList.length : (countsByTab[activeTab] ?? 0);

    // ===================== Drag & Drop for tabs =====================
    const [dragKey, setDragKey] = useState(null);

    const onDragStartTab = (e, t) => {
        if (!t?.key) return;
        // disallow dragging "Other" away from last (context enforces anyway)
        if (t.key === "OTHER") return;

        setDragKey(t.key);
        try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(t.key));
        } catch {}
    };

    const onDragOverTab = (e, t) => {
        if (!t?.key) return;
        e.preventDefault(); // allow drop
        try { e.dataTransfer.dropEffect = "move"; } catch {}
    };

    const onDropTab = (e, t) => {
        e.preventDefault();
        const from = dragKey || (() => {
            try { return e.dataTransfer.getData("text/plain"); } catch { return null; }
        })();
        const to = t?.key;
        setDragKey(null);
        if (!from || !to) return;
        if (from === to) return;
        moveTab?.(from, to);
    };

    const onDragEndTab = () => setDragKey(null);

    if (!hasScreenerAnnouncementsLoaded) {
        return (
            <div className="pf-loading-wrapper">
                <div className="pf-spinner" />
                <div className="pf-loading-text">Loading Screener Announcements…</div>
            </div>
        );
    }

    return (
        <div className="screener-annc-feed">
            <div className="annc-tabs" role="tablist" aria-label="Announcement tabs">
                {tabRegistry.map((t) => (
                    <button
                        key={t.key || t.name}
                        role="tab"
                        aria-selected={activeTab === t.name}
                        className={`annc-tab ${activeTab === t.name ? "is-active" : ""} ${dragKey === t.key ? "is-dragging" : ""}`}
                        onClick={() => setActiveTab(t.name)}                        onDragOver={(e) => onDragOverTab(e, t)}
                        onDrop={(e) => onDropTab(e, t)}                    >
                        <span
                            className={`annc-tab-handle ${t.key === "OTHER" ? "is-disabled" : ""}`}
                            draggable={t.key !== "OTHER"}
                            onDragStart={(e) => onDragStartTab(e, t)}
                            onDragEnd={onDragEndTab}
                            onClick={(e) => e.stopPropagation()}                        >
                            ⠿
                        </span>
                        <span className="annc-tab-name">{t.name}</span>
                        <span className="annc-tab-count"> ({countsByTab[t.name] ?? 0})</span>
                    </button>
                ))}
                <button
                    className="annc-tab annc-tab-new"
                    onClick={() => { setBuilderInitial(null); setShowBuilder(true); }}
                    title="Create a new tab with filters"
                >
                    + New Tab
                </button>
            </div>

            <div className="annc-panel">
                <div className="annc-feed-header">
                    <div className="annc-title">
                        {/* Adaptive count badge */}
                        <span className={`annc-count-badge ${hasDateFilter ? "is-filtered" : ""}`}>
                            {headerCount} {headerCount === 1 ? "announcement" : "announcements"}
                        </span>

                        {/* date range pill */}
                        {hasDateFilter && (
                            <span className="date-filter-pill" title="Date filter active">
                                {dateFrom && formatLocalDate(dateFrom)}{dateFrom && dateTo ? " – " : ""}
                                {!dateFrom && dateTo ? formatLocalDate(toKey) : (dateTo && formatLocalDate(dateTo))}
                            </span>
                        )}
                    </div>

                    {/* Right side actions */}
                    <div className="annc-actions" ref={menuRef}>
                        <button
                            className={`date-filter-clear always-visible ${hasDateFilter ? "" : "disabled"}`}
                            onClick={clearDateFilter}
                            title={hasDateFilter ? "Clear date filter" : "No date filter applied"}
                            disabled={!hasDateFilter}
                        >
                            Clear filter
                        </button>

                        <button
                            className="menu-toggle"
                            onClick={() => setMenuOpen((s) => !s)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            title="Actions"
                        >
                            ⋯
                        </button>

                        {menuOpen && (
                            <div className="menu-pop" role="menu">
                                <button
                                    className="menu-item"
                                    role="menuitem"
                                    onClick={() => {
                                        setDatePickerOpen(true);
                                        setMenuOpen(false);
                                    }}
                                >
                                    Filter by date
                                </button>

                                <button
                                    className="menu-item"
                                    role="menuitem"
                                    disabled={filteredList.length === 0}
                                    onClick={() => {
                                        const count = filteredList.length;
                                        if (!count) return;
                                        setMenuOpen(false);
                                        setConfirmCfg({
                                            title: "Mark all as read?",
                                            message: `Mark ${count} visible announcements as read?`,
                                            onConfirm: () => { setShowConfirm(false); handleMarkAllVisibleOld(); }
                                        });
                                        setShowConfirm(true);
                                    }}
                                >
                                    Mark all as read
                                </button>

                                <button
                                    className="menu-item"
                                    role="menuitem"
                                    disabled={!isFilterTab}
                                    onClick={() => {
                                        if (!isFilterTab) return;
                                        setMenuOpen(false);
                                        openEditTab();
                                    }}
                                >
                                    Edit tab
                                </button>

                                <button
                                    className="menu-item danger"
                                    role="menuitem"
                                    disabled={!isFilterTab}
                                    onClick={() => {
                                        if (!isFilterTab) return;
                                        setMenuOpen(false);
                                        setConfirmCfg({
                                            title: "Delete tab?",
                                            message: `This will remove the "${activeTab}" tab. This cannot be undone.`,
                                            onConfirm: () => { setShowConfirm(false); handleDeleteTab(); }
                                        });
                                        setShowConfirm(true);
                                    }}
                                >
                                    Delete tab
                                </button>
                            </div>
                        )}

                        {/* Date range popover */}
                        {datePickerOpen && (
                            <div className="date-filter-pop anchored-to-actions">
                                <div className="df-row">
                                    <label>
                                        From
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                        />
                                    </label>
                                    <label>
                                        To
                                        <input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                        />
                                    </label>
                                </div>
                                <div className="df-actions">
                                    <button className="btn-primary" onClick={() => setDatePickerOpen(false)}>Apply</button>
                                    <button className="btn-secondary" onClick={clearDateFilter}>Clear</button>
                                </div>
                                <div className="df-hint">
                                    • Fill only <b>From</b> (or only <b>To</b>) to show that single day.
                                    • Fill both for an inclusive range.
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="announcement-list-div">
                    <ul className="announcement-list">
                        {!filteredList || filteredList.length === 0 ? (
                            !hasScreenerAnnouncementsLoaded ? (
                                <div className="pf-loading-wrapper">
                                    <div className="pf-spinner" />
                                    <div className="pf-loading-text">Loading Screener Announcements…</div>
                                </div>
                            ) : (
                                <li className="announcement-empty">No announcements</li>
                            )
                        ) : (
                            filteredList.map((a, idx) => {
                                const badge = a.alertType ? badgeInfo(a.alertType) : null;
                                const key = idOf(a) || `row-${idx}`;
                                return (
                                    <li key={key} className="announcement-item">
                                        <div className="ann-row">
                                            <div className="ann-row-left-tick">
                                                <div className="ann-select">
                                                        <span
                                                            className="tick-icon"
                                                            onClick={() => handleClearOne(a)}
                                                            title="Mark as read"
                                                            role="button"
                                                            aria-label="Mark as read"
                                                        >
                                                          ✓
                                                        </span>
                                                </div>
                                            </div>
                                            <div className="ann-row-scr">
                                                <div className="superStarCompany-meta">
                                                    <div className="ann-superStarCompany">
                                                        {a.stockLink ? (
                                                            <a
                                                                href={a.stockLink}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="ann-superStarCompany-link"
                                                                title={a.screenerCompanyName}
                                                            >
                                                                {a.screenerCompanyName}
                                                            </a>
                                                        ) : (
                                                            <span className="ann-superStarCompany-text">{a.screenerCompanyName}</span>
                                                        )}
                                                    </div>

                                                    <div className="ann-meta">
                                                        {a.announcementDate && (
                                                            <span className="ann-date">{formatLocalDate(a.announcementDate)}</span>
                                                        )}
                                                        {a.timeAnnounced && <span className="ann-dot"> • </span>}
                                                        {a.timeAnnounced && <span className="ann-ago">{a.timeAnnounced}</span>}
                                                    </div>

                                                    <div className="ann-tag-col">
                                                        {badge && <span className={`ann-badge ${badge.cls}`}>{badge.text}</span>}
                                                    </div>
                                                </div>

                                                <div className="ann-subject">
                                                    {a.announcementAttachmentLink ? (
                                                        <a
                                                            href={a.announcementAttachmentLink}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="ann-subject-link"
                                                            title={a.announcementSubject}
                                                        >
                                                            {a.announcementSubject || "(No subject)"}
                                                        </a>
                                                    ) : (
                                                        <span className="ann-subject-text">
                                                          {a.announcementSubject || "(No subject)"}
                                                        </span>
                                                    )}
                                                </div>

                                                {a.aiAnnouncementSummary && (
                                                    <div className="ann-summary">{a.aiAnnouncementSummary}</div>
                                                )}
                                            </div>
                                            {a.superstarHolders?.length > 0 && (
                                                <div className="holders-side">
                                                    {a.superstarHolders.map((holder, idx) => (
                                                        <div key={idx} className="holder-chip">
                                                            {holder.superstarName}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            </div>

            {showBuilder && (
                <TabBuilder
                    onClose={() => setShowBuilder(false)}
                    onSave={builderInitial ? handleSaveEdit : handleCreateTab}
                    initial={builderInitial}
                />
            )}

            <ConfirmDialog
                open={showConfirm}
                title={confirmCfg.title}
                message={confirmCfg.message}
                onCancel={() => setShowConfirm(false)}
                onConfirm={confirmCfg.onConfirm}
            />
        </div>
    );
};

export default ScreenerAnnouncement;
